import { FormEvent, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiGet, apiPost, ApiError } from '../../api/client';
import { AuthProfile } from '../../api/types';
import { errorMessage, ptBR } from '../../i18n/pt-BR';

interface UsersResponse {
  users: AuthProfile[];
}
interface UserResponse {
  user: AuthProfile;
}

export default function UsersPage() {
  const queryClient = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'users'],
    queryFn: () => apiGet<UsersResponse>('/admin/users'),
  });

  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);

  const createMutation = useMutation({
    mutationFn: () => apiPost<UserResponse>('/admin/users', { email, name, password }),
    onSuccess: () => {
      setEmail('');
      setName('');
      setPassword('');
      void queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
    },
    onError: (err) => setError(errorMessage(err instanceof ApiError ? err.code : undefined)),
  });

  function handleCreate(e: FormEvent): void {
    e.preventDefault();
    setError(null);
    createMutation.mutate();
  }

  return (
    <section>
      <h2>{ptBR.admin.users.title}</h2>

      {isLoading && <p>{ptBR.common.loading}</p>}

      <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 24 }}>
        <thead>
          <tr style={{ textAlign: 'left', borderBottom: '1px solid #ddd' }}>
            <th>{ptBR.admin.users.name}</th>
            <th>{ptBR.admin.users.email}</th>
            <th>{ptBR.admin.users.isPlatformAdmin}</th>
          </tr>
        </thead>
        <tbody>
          {data?.users.map((user) => (
            <tr key={user.id} style={{ borderBottom: '1px solid #f0f0f0' }}>
              <td>{user.name}</td>
              <td>{user.email}</td>
              <td>{user.isPlatformAdmin ? '✓' : ''}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <h3>{ptBR.admin.users.createTitle}</h3>
      <form
        onSubmit={handleCreate}
        autoComplete="off"
        style={{ display: 'flex', gap: 8, alignItems: 'end' }}
      >
        <label>
          {ptBR.admin.users.name}
          <br />
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            autoComplete="off"
          />
        </label>
        <label>
          {ptBR.admin.users.email}
          <br />
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            autoComplete="off"
          />
        </label>
        <label>
          {ptBR.admin.users.password}
          <br />
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            minLength={8}
            autoComplete="new-password"
          />
        </label>
        <button type="submit" disabled={createMutation.isPending}>
          {ptBR.common.create}
        </button>
      </form>
      {error && <p style={{ color: '#c5221f' }}>{error}</p>}
    </section>
  );
}
